import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatDeShort } from "@/lib/date";
import {
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  CalendarRange,
  CheckCircle2,
  XCircle,
  Clock3,
  BookOpen,
  ExternalLink,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const DE_MONTHS = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];
const EN_MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DE_WD_SHORT = ["Mo","Di","Mi","Do","Fr","Sa","So"];
const EN_WD_SHORT = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

const I18N = {
  de: {
    loading: "Lädt Kalender…",
    month_view: "Monat",
    week_view: "Woche",
    prev: "Zurück",
    next: "Weiter",
    today: "Heute",
    no_class: "Keine Klasse zugeordnet.",
    week_topic: "Wochenthema",
    schedule: "Unterricht",
    diary: "Klassenbucheintrag",
    homework: "Google Classroom · Aufgaben fällig",
    no_homework: "Keine offenen Aufgaben.",
    outside: "Kein Unterrichtstag",
    weekend: "Wochenende",
    holiday: "Feiertag",
    before_start: "Vor Kursstart",
    after_end: "Nach Kursende",
    no_data: "Keine Daten für diesen Tag.",
    close: "Schließen",
    open_details: "Details öffnen",
    topics: "Themen",
    subtopics: "Details",
    lecturer: "Dozent/-in",
    week: "Woche",
    contents: "Inhalte",
    incidents: "Vorkommnisse",
    status: {
      present: "Anwesend",
      sick: "Krank",
      excused: "Entschuldigt",
      unexcused: "Unentschuldigt",
    },
    legend: {
      title: "Legende",
      present: "Anwesend",
      absent: "Fehltag",
      today: "Heute",
      taught: "Bereits behandelt",
      homework: "Aufgabe fällig",
    },
    week_of: (a, b) => `${a} – ${b}`,
    kw: "KW",
  },
  en: {
    loading: "Loading calendar…",
    month_view: "Month",
    week_view: "Week",
    prev: "Prev",
    next: "Next",
    today: "Today",
    no_class: "No class assigned.",
    week_topic: "Weekly topic",
    schedule: "Class",
    diary: "Diary entry",
    homework: "Google Classroom · Assignments due",
    no_homework: "No open assignments.",
    outside: "No class day",
    weekend: "Weekend",
    holiday: "Public holiday",
    before_start: "Before course start",
    after_end: "After course end",
    no_data: "No data for this day.",
    close: "Close",
    open_details: "Open details",
    topics: "Topics",
    subtopics: "Details",
    lecturer: "Lecturer",
    week: "Week",
    contents: "Content",
    incidents: "Incidents",
    status: {
      present: "Present",
      sick: "Sick",
      excused: "Excused",
      unexcused: "Unexcused",
    },
    legend: {
      title: "Legend",
      present: "Present",
      absent: "Absent",
      today: "Today",
      taught: "Already taught",
      homework: "Assignment due",
    },
    week_of: (a, b) => `${a} – ${b}`,
    kw: "W",
  },
};

function ymOf(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; }
function addMonths(ym, delta) {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return ymOf(d);
}
function firstMondayOnOrBefore(d) {
  const nd = new Date(d);
  const dow = (nd.getDay() + 6) % 7; // Mon=0..Sun=6
  nd.setDate(nd.getDate() - dow);
  return nd;
}
function isoOfDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function isoWeekNumber(d) {
  // ISO week number
  const target = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNr = (target.getUTCDay() + 6) % 7;
  target.setUTCDate(target.getUTCDate() - dayNr + 3);
  const firstThursday = new Date(Date.UTC(target.getUTCFullYear(), 0, 4));
  const diff = (target - firstThursday) / 86400000;
  return 1 + Math.floor(diff / 7);
}
function fmt(iso, lang) {
  if (!iso) return "—";
  if (lang === "de") return formatDeShort(iso);
  const d = new Date(iso + "T00:00:00");
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getFullYear()).slice(-2)}`;
}

const STATUS_BG = {
  present: "bg-emerald-500",
  sick: "bg-blue-500",
  excused: "bg-amber-500",
  unexcused: "bg-red-500",
};

export default function ParticipantCalendar({ lang = "de", classId, weeklyPlan = [] }) {
  const t = I18N[lang];
  const today = new Date();
  const [view, setView] = useState("month"); // "month" | "week"
  const [ym, setYm] = useState(ymOf(today));
  const [weekAnchor, setWeekAnchor] = useState(firstMondayOnOrBefore(today));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [coursework, setCoursework] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = async (targetYm) => {
    setLoading(true);
    try {
      const r = await api.get(`/weiterbildung/me/calendar`, { params: { month: targetYm } });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  const loadCoursework = async () => {
    if (!classId) return;
    try {
      const r = await api.get(`/google/classes/${classId}/coursework`);
      setCoursework(r.data?.upcoming || []);
    } catch (_) {
      // silent — no google connected or no course id: not an error for calendar
      setCoursework([]);
    }
  };

  useEffect(() => { load(ym); }, [ym]);
  useEffect(() => { loadCoursework(); }, [classId]);

  // coursework due-by-date lookup
  const cwByDate = useMemo(() => {
    const map = {};
    for (const c of coursework) {
      if (!c.due_at) continue;
      const iso = c.due_at.slice(0, 10);
      (map[iso] = map[iso] || []).push(c);
    }
    return map;
  }, [coursework]);

  // Enrich days with coursework
  const enrichedDays = useMemo(() => {
    if (!data?.days) return [];
    return data.days.map((d) => ({ ...d, coursework: cwByDate[d.date] || [] }));
  }, [data, cwByDate]);

  const daysByDate = useMemo(() => {
    const map = {};
    for (const d of enrichedDays) map[d.date] = d;
    return map;
  }, [enrichedDays]);

  // Derived from ym (safe even before data loaded)
  const [yr, mo] = ym.split("-").map(Number);

  // Month grid: start from Monday on/before day 1
  const monthGridDays = useMemo(() => {
    const first = new Date(yr, mo - 1, 1);
    const start = firstMondayOnOrBefore(first);
    const arr = [];
    const d = new Date(start);
    for (let i = 0; i < 42; i++) {
      arr.push({ iso: isoOfDate(d), inMonth: d.getMonth() === mo - 1 });
      d.setDate(d.getDate() + 1);
    }
    return arr;
  }, [yr, mo]);

  const weekDays = useMemo(() => {
    const arr = [];
    const d = new Date(weekAnchor);
    for (let i = 0; i < 7; i++) {
      arr.push(isoOfDate(d));
      d.setDate(d.getDate() + 1);
    }
    return arr;
  }, [weekAnchor]);

  const weekLabel = useMemo(() => {
    if (view !== "week") return "";
    const start = weekAnchor;
    const end = new Date(weekAnchor); end.setDate(end.getDate() + 6);
    return `${t.kw} ${isoWeekNumber(start)} · ${fmt(isoOfDate(start), lang)} – ${fmt(isoOfDate(end), lang)}`;
  }, [view, weekAnchor, lang, t]);

  if (loading && !data) return <div className="p-8 text-xs text-slate-500">{t.loading}</div>;
  if (!data?.class) return <div className="p-8 text-sm text-slate-500 italic">{t.no_class}</div>;

  const monthLabel = `${(lang === "de" ? DE_MONTHS : EN_MONTHS)[mo - 1]} ${yr}`;

  const goPrev = () => {
    if (view === "month") setYm(addMonths(ym, -1));
    else {
      const d = new Date(weekAnchor); d.setDate(d.getDate() - 7);
      setWeekAnchor(d); setYm(ymOf(d));
    }
  };
  const goNext = () => {
    if (view === "month") setYm(addMonths(ym, 1));
    else {
      const d = new Date(weekAnchor); d.setDate(d.getDate() + 7);
      setWeekAnchor(d); setYm(ymOf(d));
    }
  };
  const goToday = () => {
    setYm(ymOf(today));
    setWeekAnchor(firstMondayOnOrBefore(today));
  };

  const weekDaysShort = lang === "de" ? DE_WD_SHORT : EN_WD_SHORT;

  return (
    <div data-testid="participant-calendar">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-2">
          <button
            onClick={goPrev}
            className="border border-slate-300 hover:bg-slate-900 hover:text-white text-slate-700 h-9 w-9 flex items-center justify-center"
            data-testid="cal-prev-btn"
            aria-label={t.prev}
          >
            <ChevronLeft size={16} strokeWidth={1.5} />
          </button>
          <button
            onClick={goToday}
            className="border border-slate-300 hover:bg-slate-900 hover:text-white text-slate-700 px-3 h-9 text-xs font-medium"
            data-testid="cal-today-btn"
          >
            {t.today}
          </button>
          <button
            onClick={goNext}
            className="border border-slate-300 hover:bg-slate-900 hover:text-white text-slate-700 h-9 w-9 flex items-center justify-center"
            data-testid="cal-next-btn"
            aria-label={t.next}
          >
            <ChevronRight size={16} strokeWidth={1.5} />
          </button>
          <div className="ml-3 font-heading text-2xl font-light tracking-tighter">
            {view === "month" ? monthLabel : weekLabel}
          </div>
        </div>

        <div className="flex items-center border border-slate-200 p-1">
          <button
            onClick={() => setView("month")}
            className={`px-3 h-8 text-xs font-medium flex items-center gap-1 ${view === "month" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}
            data-testid="cal-view-month-btn"
          >
            <CalendarDays size={14} strokeWidth={1.5} /> {t.month_view}
          </button>
          <button
            onClick={() => setView("week")}
            className={`px-3 h-8 text-xs font-medium flex items-center gap-1 ${view === "week" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}
            data-testid="cal-view-week-btn"
          >
            <CalendarRange size={14} strokeWidth={1.5} /> {t.week_view}
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 items-center text-[11px] text-slate-500 mb-3 font-mono">
        <span className="font-medium uppercase tracking-overline text-slate-400">{t.legend.title}:</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-emerald-500 inline-block" /> {t.legend.present}</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-500 inline-block" /> {t.legend.absent}</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 border-2 border-slate-900 inline-block" /> {t.legend.today}</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-[#0a788c] inline-block" /> {t.legend.taught}</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-[#f5b800] inline-block" /> {t.legend.homework}</span>
      </div>

      {view === "month" ? (
        <MonthGrid
          gridDays={monthGridDays}
          daysByDate={daysByDate}
          weekDaysShort={weekDaysShort}
          onSelect={(iso) => setSelected(daysByDate[iso] || { date: iso, out_of_range: true })}
          lang={lang}
          t={t}
        />
      ) : (
        <WeekView
          weekDays={weekDays}
          daysByDate={daysByDate}
          onSelect={(iso) => setSelected(daysByDate[iso] || { date: iso, out_of_range: true })}
          lang={lang}
          t={t}
        />
      )}

      <DayDetailDialog
        day={selected}
        onClose={() => setSelected(null)}
        lang={lang}
        t={t}
      />
    </div>
  );
}

function MonthGrid({ gridDays, daysByDate, weekDaysShort, onSelect, lang, t }) {
  return (
    <div className="border border-slate-200" data-testid="cal-month-grid">
      <div className="grid grid-cols-7 border-b border-slate-200">
        {weekDaysShort.map((w) => (
          <div key={w} className="text-[10px] font-bold uppercase tracking-overline text-slate-500 px-2 py-2 text-center border-r border-slate-100 last:border-r-0">
            {w}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {gridDays.map((cell, idx) => {
          const d = daysByDate[cell.iso];
          return (
            <DayCell
              key={cell.iso + idx}
              iso={cell.iso}
              inMonth={cell.inMonth}
              d={d}
              onSelect={onSelect}
              lang={lang}
              t={t}
            />
          );
        })}
      </div>
    </div>
  );
}

function DayCell({ iso, inMonth, d, onSelect, lang, t }) {
  const day = d || { date: iso };
  const num = parseInt(iso.slice(8, 10), 10);
  const isToday = d?.is_today;
  const isSchoolDay = d?.is_school_day;
  const status = d?.attendance?.status;
  const hasHomework = (d?.coursework || []).length > 0;
  const taught = d?.week_taught;

  return (
    <button
      onClick={() => onSelect(iso)}
      data-testid={`cal-day-${iso}`}
      className={`text-left border-r border-b border-slate-100 last:border-r-0 min-h-[92px] p-2 flex flex-col relative
        ${!inMonth ? "bg-slate-50/70 opacity-50" : "bg-white hover:bg-slate-50"}
        ${isToday ? "outline outline-2 outline-slate-900 outline-offset-[-2px] z-10" : ""}
      `}
    >
      <div className="flex items-baseline justify-between">
        <span className={`text-xs font-mono ${isToday ? "font-bold text-slate-900" : "text-slate-600"}`}>{num}</span>
        {d?.week_number && inMonth && (
          <span className="text-[9px] font-mono uppercase tracking-overline text-slate-400">
            {t.kw}{d.week_number}
          </span>
        )}
      </div>
      {inMonth && (
        <>
          {isSchoolDay && (
            <div className="mt-1 text-[10px] leading-tight text-slate-800 line-clamp-2">
              {d?.week_theme || ""}
            </div>
          )}
          <div className="mt-auto flex items-center gap-1 pt-1">
            {status && (
              <span
                title={t.status[status] || status}
                className={`w-2 h-2 rounded-full ${STATUS_BG[status] || "bg-slate-400"}`}
              />
            )}
            {taught && isSchoolDay && !status && (
              <span title={t.legend.taught} className="w-2 h-2 rounded-full bg-[#0a788c]" />
            )}
            {hasHomework && (
              <span title={t.legend.homework} className="w-2 h-2 rounded-full bg-[#f5b800]" />
            )}
            {d?.is_holiday && (
              <span className="text-[9px] uppercase tracking-overline text-red-500 ml-auto">
                {t.holiday}
              </span>
            )}
          </div>
        </>
      )}
    </button>
  );
}

function WeekView({ weekDays, daysByDate, onSelect, lang, t }) {
  return (
    <div className="border border-slate-200 divide-y divide-slate-100" data-testid="cal-week-grid">
      {weekDays.map((iso) => {
        const d = daysByDate[iso];
        const day = new Date(iso + "T00:00:00");
        const wd = (lang === "de" ? DE_WD_SHORT : EN_WD_SHORT)[(day.getDay() + 6) % 7];
        const status = d?.attendance?.status;
        return (
          <button
            key={iso}
            onClick={() => onSelect(iso)}
            data-testid={`cal-week-day-${iso}`}
            className={`w-full text-left p-4 flex items-start gap-4 hover:bg-slate-50 ${d?.is_today ? "bg-slate-100" : ""}`}
          >
            <div className="w-14 shrink-0 text-center border-r border-slate-100 pr-3">
              <div className="text-[10px] uppercase text-slate-500">{wd}</div>
              <div className={`font-heading text-2xl font-light ${d?.is_today ? "text-slate-900" : "text-slate-700"}`}>{iso.slice(8, 10)}</div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2 flex-wrap">
                <div className="text-sm font-medium text-slate-900">
                  {d?.week_theme || (d?.is_school_day ? "—" : (d?.is_holiday ? t.holiday : (d?.is_weekend ? t.weekend : t.outside)))}
                </div>
                {d?.week_number && (
                  <span className="text-[10px] font-mono uppercase tracking-overline text-slate-400">
                    {t.kw}{d.week_number}
                  </span>
                )}
                {status && (
                  <span className={`text-[10px] px-2 py-0.5 text-white ${STATUS_BG[status]}`}>
                    {t.status[status] || status}
                  </span>
                )}
              </div>
              {d?.is_school_day && (
                <div className="text-[11px] font-mono text-slate-500 mt-1 flex items-center gap-1">
                  <Clock3 size={11} strokeWidth={1.5} /> {d.time_from || "?"}–{d.time_to || "?"}
                </div>
              )}
              {(d?.week_topics || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {(d.week_topics || []).slice(0, 4).map((tp, i) => (
                    <span key={i} className="text-[10px] px-2 py-0.5 bg-slate-900 text-white">{tp}</span>
                  ))}
                </div>
              )}
              {(d?.coursework || []).length > 0 && (
                <div className="mt-2 text-[11px] text-[#f5b800] flex items-center gap-1">
                  <BookOpen size={12} strokeWidth={1.5} /> {d.coursework.length} × {t.legend.homework}
                </div>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function DayDetailDialog({ day, onClose, lang, t }) {
  const open = !!day;
  if (!open) return <Dialog open={false} onOpenChange={onClose}><DialogContent /></Dialog>;
  const iso = day.date;
  const d = new Date(iso + "T00:00:00");
  const wd = (lang === "de" ? ["Sonntag","Montag","Dienstag","Mittwoch","Donnerstag","Freitag","Samstag"] : ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"])[d.getDay()];
  const mo = (lang === "de" ? DE_MONTHS : EN_MONTHS)[d.getMonth()];
  const title = `${wd}, ${d.getDate()}. ${mo} ${d.getFullYear()}`;
  const status = day.attendance?.status;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-none border border-slate-200 p-0" data-testid="cal-day-dialog">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-slate-100">
          <div className="text-xs uppercase text-slate-500 font-mono">{iso}</div>
          <DialogTitle className="font-heading text-2xl font-light tracking-tighter">{title}</DialogTitle>
          <DialogDescription className="sr-only">Details für {iso}</DialogDescription>
        </DialogHeader>
        <div className="px-6 py-5 space-y-4 text-sm max-h-[70vh] overflow-y-auto">
          {!day.in_class_range && !day.out_of_range && (
            <div className="text-slate-500 italic">
              {day.date < (day.class_start || "0000") ? t.before_start : t.after_end}
            </div>
          )}
          {day.is_holiday && (
            <div className="border-l-4 border-red-500 bg-red-50 text-red-800 px-4 py-2 text-xs">{t.holiday}</div>
          )}
          {day.is_weekend && !day.is_school_day && day.in_class_range && (
            <div className="text-slate-500 italic text-xs">{t.weekend}</div>
          )}

          {/* Week topic */}
          {day.week_theme && (
            <div>
              <div className="text-[10px] uppercase text-slate-500 font-bold tracking-overline mb-1">
                {t.week_topic} · {t.kw}{day.week_number}
              </div>
              <div className="font-medium">{day.week_theme}</div>
              {(day.week_topics || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {day.week_topics.map((tp, i) => (
                    <span key={i} className="text-[11px] px-2 py-1 bg-slate-900 text-white">{tp}</span>
                  ))}
                </div>
              )}
              {(day.week_subtopics || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {day.week_subtopics.map((tp, i) => (
                    <span key={i} className="text-[11px] px-2 py-1 border border-slate-300 text-slate-700 bg-white">{tp}</span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Schedule + attendance status */}
          {day.is_school_day && (
            <div className="border border-slate-200 p-4">
              <div className="text-[10px] uppercase text-slate-500 font-bold tracking-overline mb-1">{t.schedule}</div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-sm">{day.time_from || "?"}–{day.time_to || "?"}</span>
                {status && (
                  <span className={`text-[10px] px-2 py-0.5 text-white ${STATUS_BG[status]} flex items-center gap-1`}>
                    {status === "present" ? <CheckCircle2 size={12} strokeWidth={1.5} /> : <XCircle size={12} strokeWidth={1.5} />}
                    {t.status[status] || status}
                  </span>
                )}
              </div>
              {day.attendance?.lecturer_name && (
                <div className="text-xs text-slate-500 mt-1">{t.lecturer}: {day.attendance.lecturer_name}</div>
              )}
            </div>
          )}

          {/* Diary */}
          {day.diary && (
            <div className="border border-slate-200 p-4">
              <div className="text-[10px] uppercase text-slate-500 font-bold tracking-overline mb-1">{t.diary}</div>
              {day.diary.topics && (
                <div className="text-sm"><span className="font-medium text-slate-500 uppercase text-[10px] mr-2">{t.topics}:</span>{day.diary.topics}</div>
              )}
              {day.diary.contents && (
                <div className="text-sm mt-1"><span className="font-medium text-slate-500 uppercase text-[10px] mr-2">{t.contents}:</span>{day.diary.contents}</div>
              )}
              {day.diary.incidents && (
                <div className="text-sm mt-1 text-amber-800"><span className="font-medium text-slate-500 uppercase text-[10px] mr-2">{t.incidents}:</span>{day.diary.incidents}</div>
              )}
            </div>
          )}

          {/* Coursework */}
          {(day.coursework || []).length > 0 && (
            <div className="border border-[#f5b800] p-4 bg-[#fff9e6]">
              <div className="text-[10px] uppercase text-slate-700 font-bold tracking-overline mb-2">{t.homework}</div>
              <ul className="space-y-2">
                {day.coursework.map((c) => (
                  <li key={c.id} className="flex items-start gap-2 text-sm" data-testid={`cw-item-${c.id}`}>
                    <BookOpen size={14} strokeWidth={1.5} className="mt-0.5 shrink-0 text-slate-700" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-900">{c.title}</div>
                      {c.description && <div className="text-xs text-slate-600 mt-0.5 line-clamp-2">{c.description}</div>}
                    </div>
                    {c.alternate_link && (
                      <a
                        href={c.alternate_link}
                        target="_blank" rel="noopener noreferrer"
                        className="text-[#1a73e8] hover:underline text-xs flex items-center gap-1 shrink-0"
                      >
                        <ExternalLink size={12} strokeWidth={1.5} /> Google
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!day.week_theme && !day.is_school_day && !day.diary && !day.is_holiday && (
            <div className="text-slate-400 italic text-xs">{t.no_data}</div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-slate-300 hover:bg-slate-900 hover:text-white"
            data-testid="cal-dialog-close-btn"
          >
            {t.close}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
